// BMG Theme JS — Bootstrap + Scroll Reveal

/**
 * BMG Theme JavaScript
 *
 * Includes Bootstrap 5 and custom functionality.
 * Refined luxury editorial aesthetic.
 */

// Import Bootstrap (side-effect: registers the collapse/dropdown data-api used by
// the navbar toggler) plus the Collapse class for programmatic menu control.
import 'bootstrap';
import { Collapse } from 'bootstrap';

/**
 * Skip Link Focus Fix
 * Helps with accessibility for keyboard only users.
 */
( function() {
	const isIe = /(trident|msie)/i.test( navigator.userAgent );

	if ( isIe && document.getElementById && window.addEventListener ) {
		window.addEventListener(
			'hashchange',
			function() {
				const id = location.hash.substring( 1 );
				let element;

				if ( ! /^[A-z0-9_-]+$/.test( id ) ) {
					return;
				}

				element = document.getElementById( id );

				if ( element ) {
					if ( ! /^(?:a|select|input|button|textarea)$/i.test( element.tagName ) ) {
						element.tabIndex = -1;
					}

					element.focus();
				}
			},
			false
		);
	}
} )();

/**
 * Scroll Reveal
 * Uses IntersectionObserver to reveal elements as they enter the viewport.
 * Adds .revealed class to elements with .reveal-on-scroll
 */
( function() {
	'use strict';

	function init() {
		// Check for reduced motion preference
		const prefersReducedMotion = window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

		if ( prefersReducedMotion ) {
			// If user prefers reduced motion, reveal all elements immediately
			document.querySelectorAll( '.reveal-on-scroll' ).forEach( function( el ) {
				el.classList.add( 'revealed' );
			} );
			return;
		}

		// Check for IntersectionObserver support
		if ( ! ( 'IntersectionObserver' in window ) ) {
			// Fallback: reveal all elements
			document.querySelectorAll( '.reveal-on-scroll' ).forEach( function( el ) {
				el.classList.add( 'revealed' );
			} );
			return;
		}

		const observerOptions = {
			root: null, // viewport
			rootMargin: '0px 0px -50px 0px', // Trigger slightly before element is fully visible
			threshold: 0.1 // 10% visible
		};

		const observer = new IntersectionObserver( function( entries ) {
			entries.forEach( function( entry ) {
				if ( entry.isIntersecting ) {
					entry.target.classList.add( 'revealed' );
					// Stop observing once revealed (one-time animation)
					observer.unobserve( entry.target );
				}
			} );
		}, observerOptions );

		// Observe all elements with .reveal-on-scroll
		document.querySelectorAll( '.reveal-on-scroll' ).forEach( function( el ) {
			observer.observe( el );
		} );
	}

	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', init );
	} else {
		init();
	}
} )();

/**
 * BMG Reveal System
 * Uses IntersectionObserver to reveal elements with .bmg-reveal class.
 * Adds .is-visible when element enters viewport at 15% threshold.
 * One-way — once revealed, stays revealed.
 */
( function() {
	'use strict';

	function init() {
		const prefersReducedMotion = window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;
		const reveals = document.querySelectorAll( '.bmg-reveal' );

		if ( ! reveals.length ) {
			return;
		}

		if ( prefersReducedMotion || ! ( 'IntersectionObserver' in window ) ) {
			reveals.forEach( function( el ) {
				el.classList.add( 'is-visible' );
			} );
			return;
		}

		const observer = new IntersectionObserver( function( entries ) {
			entries.forEach( function( entry ) {
				if ( entry.isIntersecting ) {
					entry.target.classList.add( 'is-visible' );
					observer.unobserve( entry.target );
				}
			} );
		}, { threshold: 0.15 } );

		reveals.forEach( function( el ) {
			observer.observe( el );
		} );
	}

	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', init );
	} else {
		init();
	}
} )();

/**
 * Smooth Scroll
 * Handles smooth scrolling for anchor links with header offset.
 */
( function() {
	'use strict';

	function init() {
		document.querySelectorAll( 'a[href^="#"]' ).forEach( function( anchor ) {
			anchor.addEventListener( 'click', function( e ) {
				const targetId = this.getAttribute( 'href' );

				if ( targetId === '#' ) {
					return;
				}

				const target = document.querySelector( targetId );

				if ( target ) {
					e.preventDefault();
					const headerOffset = 80;
					const elementPosition = target.getBoundingClientRect().top;
					const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

					window.scrollTo( {
						top: offsetPosition,
						behavior: 'smooth'
					} );
				}
			} );
		} );
	}

	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', init );
	} else {
		init();
	}
} )();

/**
 * Mobile Nav — collapse the menu after a link is chosen.
 *
 * The open dropdown (#navbarNavDropdown) otherwise stays expanded after a nav-link
 * tap, covering the section the anchor jumps to. Close it via Bootstrap's Collapse
 * API on any in-menu link click — but only while the menu is actually expanded
 * (.show), so the always-visible desktop menu (≥992px, never .show) is left alone.
 * Submenu parents (.dropdown-toggle) are skipped so they can open their submenu.
 */
( function() {
	'use strict';

	function init() {
		const menu = document.getElementById( 'navbarNavDropdown' );

		if ( ! menu ) {
			return;
		}

		menu.addEventListener( 'click', function( e ) {
			const link = e.target.closest ? e.target.closest( '.nav-link' ) : null;

			if ( ! link || ! menu.contains( link ) ) {
				return;
			}
			if ( link.classList.contains( 'dropdown-toggle' ) ) {
				return; // submenu toggle — let it expand, don't dismiss the menu
			}
			if ( ! menu.classList.contains( 'show' ) ) {
				return; // desktop layout or already closed
			}

			Collapse.getOrCreateInstance( menu ).hide();
		} );
	}

	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', init );
	} else {
		init();
	}
} )();

/**
 * FAQ Accordion (#faq, .sh-faq)
 *
 * Single-open accordion: opening one question closes the others. State lives on
 * each question <button>'s aria-expanded — the panel open/close and chevron
 * rotation are handled entirely in CSS off that attribute (see .sh-faq). Native
 * <button> semantics give Enter/Space activation and Tab navigation for free, so
 * this only flips aria-expanded on click and enforces the one-open-at-a-time rule.
 */
( function() {
	'use strict';

	function init() {
		const buttons = document.querySelectorAll( '.sh-faq__q' );

		if ( ! buttons.length ) {
			return;
		}

		buttons.forEach( function( button ) {
			button.addEventListener( 'click', function() {
				const willOpen = button.getAttribute( 'aria-expanded' ) !== 'true';

				// Close every question, then open the clicked one if it was closed.
				buttons.forEach( function( other ) {
					other.setAttribute( 'aria-expanded', 'false' );
				} );

				button.setAttribute( 'aria-expanded', willOpen ? 'true' : 'false' );
			} );
		} );
	}

	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', init );
	} else {
		init();
	}
} )();

/**
 * Back to Top Button
 * Shows a fixed button after scrolling 2x viewport height.
 * Smooth scrolls to top on click. Respects prefers-reduced-motion.
 */
( function() {
	'use strict';

	function init() {
		var backToTop = document.querySelector( '.bmg-back-to-top' );

		if ( ! backToTop ) {
			return;
		}

		var threshold = window.innerHeight * 2;
		var prefersReducedMotion = window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

		window.addEventListener( 'scroll', function() {
			if ( window.scrollY > threshold ) {
				backToTop.classList.add( 'is-visible' );
			} else {
				backToTop.classList.remove( 'is-visible' );
			}
		}, { passive: true } );

		backToTop.addEventListener( 'click', function() {
			window.scrollTo( {
				top: 0,
				behavior: prefersReducedMotion ? 'auto' : 'smooth'
			} );
		} );
	}

	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', init );
	} else {
		init();
	}
} )();

/**
 * Sticky Mobile CTA (.sh-sticky-cta)
 *
 * The bar is hidden on tablet/desktop by CSS; this only drives its scroll reveal and
 * dismiss on phones. It stays hidden near the top (so it doesn't fight the hero CTAs),
 * appears once the user is past ~300px, and follows the standard pattern: tuck away
 * while actively scrolling down, return on scroll-up or when the scroll settles.
 *
 * Dismiss is session-scoped via a session cookie (no expiry — matches the age-gate
 * pattern), so the bar stays hidden for the rest of the session and returns in a fresh
 * session/private window. Reduced motion is handled in CSS (no transition).
 */
( function() {
	'use strict';

	var COOKIE = 'sh_sticky_cta_dismissed';

	function init() {
		var bar = document.querySelector( '.sh-sticky-cta' );

		if ( ! bar ) {
			return;
		}

		// Session-cookie dismiss — matches the CBD age gate (session-scoped, not persisted).
		if ( document.cookie.indexOf( COOKIE + '=1' ) !== -1 ) {
			bar.remove();
			return;
		}

		var lastY   = window.scrollY;
		var ticking = false;

		function update() {
			var y = window.scrollY;

			if ( y <= 300 ) {
				bar.classList.remove( 'is-visible' ); // near the top — stay out of the hero's way
			} else if ( y > lastY + 5 ) {
				bar.classList.remove( 'is-visible' ); // scrolling down — tuck away
			} else {
				bar.classList.add( 'is-visible' );    // scrolling up or settled — reveal
			}

			lastY   = y;
			ticking = false;
		}

		window.addEventListener( 'scroll', function() {
			if ( ! ticking ) {
				window.requestAnimationFrame( update );
				ticking = true;
			}
		}, { passive: true } );

		var dismiss = bar.querySelector( '.sh-sticky-cta__dismiss' );

		if ( dismiss ) {
			dismiss.addEventListener( 'click', function() {
				document.cookie = COOKIE + '=1; path=/; SameSite=Lax';
				bar.classList.add( 'is-dismissed' );
				bar.classList.remove( 'is-visible' );
				window.setTimeout( function() { bar.remove(); }, 300 );
			} );
		}
	}

	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', init );
	} else {
		init();
	}
} )();

/**
 * Hero Parallax (homepage #hero).
 *
 * The hero background (.section-hero__background) is a separate DOM layer, so it
 * can translate independently of the content. It scrolls at ~0.45x foreground
 * speed for depth. GPU-accelerated via translate3d (never background-position),
 * throttled through requestAnimationFrame.
 *
 * Guards (match _sections.scss): DESKTOP ONLY (≥768px) and reduced-motion OFF —
 * mobile and reduced-motion keep the static cover background. The paired CSS
 * grows the layer 10% past each edge so translating never reveals a bare edge.
 */
( function() {
	'use strict';

	var SPEED = 0.45;

	function init() {
		if ( window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches ) { return; }
		if ( window.innerWidth < 768 ) { return; }

		var bg = document.querySelector( '.section-hero__background' );
		if ( ! bg ) { return; }

		var hero   = bg.parentElement;
		var ticking = false;

		function update() {
			ticking = false;
			var scrollY = window.scrollY;
			// Stop once the hero has scrolled fully out of view.
			if ( scrollY > hero.offsetHeight ) { return; }
			bg.style.transform = 'translate3d(0, ' + ( scrollY * SPEED ) + 'px, 0)';
		}

		window.addEventListener( 'scroll', function() {
			if ( ! ticking ) {
				requestAnimationFrame( update );
				ticking = true;
			}
		}, { passive: true } );

		update(); // set initial position (e.g. reload mid-page)
	}

	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', init );
	} else {
		init();
	}
} )();

/**
 * Order Pad — RESERVE ONLINE, PAY IN-STORE (homepage #order).
 *
 * BUILD-THEN-ADD (Model A): tapping an item makes it the ACTIVE item being composed
 * (it does NOT drop into the order). While active, the modifier controls bind to IT —
 * liquid (smoothies), base (bowls), and boost chips. "Add to Order" commits the active
 * item WITH its modifiers as ONE line, then resets. Switching tabs or picking a
 * different item discards any in-progress selection. So modifiers belong to a LINE
 * ITEM, never to the whole order (no leak across items). Client-side only; the final
 * action is submitReservation() — a SEAM (TODO) that does NOT send.
 */
( function() {
	'use strict';

	function money( n ) {
		return '$' + ( Math.round( n * 100 ) / 100 ).toFixed( 2 );
	}

	var lineSeq = 0;

	function init() {
		var pad = document.querySelector( '.order-pad' );
		if ( ! pad ) { return; }

		var tabs        = Array.prototype.slice.call( pad.querySelectorAll( '[data-op-tab]' ) );
		var itemBoxes   = Array.prototype.slice.call( pad.querySelectorAll( '[data-op-item]' ) );
		var chips       = Array.prototype.slice.call( pad.querySelectorAll( '[data-op-boost]' ) );
		var summaryEl   = pad.querySelector( '[data-op-summary]' );
		var emptyEl     = pad.querySelector( '[data-op-empty]' );
		var subtotalEl  = pad.querySelector( '[data-op-subtotal]' );
		var submitBtn   = pad.querySelector( '[data-op-submit]' );
		var submitLabel = pad.querySelector( '[data-op-submit-label]' );
		var nameInput   = pad.querySelector( '[data-op-cust-name]' );
		var phoneInput  = pad.querySelector( '[data-op-phone]' );
		var emailInput  = pad.querySelector( '[data-op-email]' );
		var pickupInput = pad.querySelector( '[data-op-pickup]' );
		var notesInput  = pad.querySelector( '[data-op-notes]' );
		var nameErr     = pad.querySelector( '[data-op-error="name"]' );
		var phoneErr    = pad.querySelector( '[data-op-error="phone"]' );

		// Compose panel refs.
		var hintEl      = pad.querySelector( '[data-op-compose-hint]' );
		var activeWrap  = pad.querySelector( '[data-op-compose-active]' );
		var activeName  = pad.querySelector( '[data-op-active-name]' );
		var liquidGroup = pad.querySelector( '[data-op-liquid-group]' );
		var boostsWrap  = pad.querySelector( '[data-op-boosts-wrap]' );
		var addBtn      = pad.querySelector( '[data-op-add]' );
		var lineTotalEl = pad.querySelector( '[data-op-line-total]' );
		var announceEl  = pad.querySelector( '[data-op-announce]' );

		var opClosed = pad.hasAttribute( 'data-op-closed' );

		// Tab browsing works in every state — including read-only mode when the store
		// is closed — so wire it up here, ahead of the ordering controls, and it
		// survives the early return below. setTab / clearActive are hoisted.
		tabs.forEach( function( t ) {
			t.addEventListener( 'click', function() { setTab( t.getAttribute( 'data-op-tab' ) ); } );
		} );
		var opInitialTab = tabs.length ? tabs[ 0 ].getAttribute( 'data-op-tab' ) : '';
		if ( opInitialTab ) { setTab( opInitialTab ); }

		// Ordering is gated: skip all of it when the store is closed (read-only menu)
		// or when the ordering controls aren't present in the DOM.
		if ( opClosed || ! summaryEl || ! submitBtn || ! addBtn ) { return; }

		var ctaLabel = submitBtn.getAttribute( 'data-op-cta-label' ) || 'Reserve for Pickup';

		// Eligible modifier categories for the active item (from data-op-mods).
		function activeMods() {
			if ( ! activeBox ) { return []; }
			return ( activeBox.getAttribute( 'data-op-mods' ) || '' ).split( ',' ).filter( Boolean );
		}

		// State: the ACTIVE (in-progress) item + its modifiers, and the committed lines.
		var activeBox    = null;              // element being composed, or null
		var activeBoosts = {};                // id → {name, price} for the active item
		var lines        = [];                // committed { lineId, name, price, tab, mod, boosts:[], dotColor }

		function announce( msg ) {
			if ( announceEl ) { announceEl.textContent = msg; }
		}

		// ---- Modifier controls (bound to the active item) ----
		function resetModifiers() {
			activeBoosts = {};
			chips.forEach( function( c ) {
				c.classList.remove( 'is-active' );
				c.setAttribute( 'aria-pressed', 'false' );
			} );
			// Reset the liquid radios to their first option.
			pad.querySelectorAll( '[data-op-liquid]' ).forEach( function( r, i ) { r.checked = ( i === 0 ); } );
		}

		function checkedValue( selector ) {
			var r = pad.querySelector( selector + ':checked' );
			return r ? r.value : '';
		}

		function activeModifier() {
			if ( ! activeBox ) { return ''; }
			// The required liquid is the only single-select modifier (smoothies).
			if ( activeMods().indexOf( 'liquids' ) !== -1 ) { return checkedValue( '[data-op-liquid]' ); }
			return '';
		}
		function activeLiquidId() {
			if ( ! activeBox || activeMods().indexOf( 'liquids' ) === -1 ) { return 0; }
			var r = pad.querySelector( '[data-op-liquid]:checked' );
			return r ? ( parseInt( r.getAttribute( 'data-op-id' ), 10 ) || 0 ) : 0;
		}

		function boostArray() {
			return Object.keys( activeBoosts ).map( function( id ) { return activeBoosts[ id ]; } );
		}

		function activePrice() {
			if ( ! activeBox ) { return 0; }
			var base = parseFloat( activeBox.getAttribute( 'data-op-price' ) ) || 0;
			return boostArray().reduce( function( s, b ) { return s + b.price; }, base );
		}

		function renderLineTotal() {
			if ( lineTotalEl ) { lineTotalEl.textContent = money( activePrice() ); }
		}

		// ---- Active item ----
		function clearActive( discard ) {
			if ( activeBox ) {
				activeBox.classList.remove( 'op-item--active' );
				activeBox.setAttribute( 'aria-pressed', 'false' );
				activeBox.removeAttribute( 'aria-current' );
			}
			activeBox = null;
			resetModifiers();
			if ( activeWrap ) { activeWrap.hidden = true; }
			if ( hintEl ) { hintEl.hidden = false; }
			if ( discard ) { announce( 'Selection cleared.' ); }
		}

		function setActive( box, viaKeyboard ) {
			if ( box === activeBox ) { return; }
			clearActive( false ); // discard any in-progress compose (a different thing now)

			activeBox = box;
			box.classList.add( 'op-item--active' );
			box.setAttribute( 'aria-pressed', 'true' );
			box.setAttribute( 'aria-current', 'true' );

			var name = box.getAttribute( 'data-op-name' ) || '';
			if ( activeName ) { activeName.textContent = name; }

			// Gate modifier controls to the active item's eligibility (data-op-mods).
			var mods = activeMods();
			if ( liquidGroup ) { liquidGroup.hidden = mods.indexOf( 'liquids' ) === -1; }
			var anyChip = false;
			chips.forEach( function( chip ) {
				var eligible = mods.indexOf( chip.getAttribute( 'data-op-modcat' ) ) !== -1;
				chip.style.display = eligible ? '' : 'none';
				if ( eligible ) { anyChip = true; }
			} );
			if ( boostsWrap ) { boostsWrap.style.display = anyChip ? '' : 'none'; }

			if ( hintEl ) { hintEl.hidden = true; }
			if ( activeWrap ) { activeWrap.hidden = false; }
			renderLineTotal();
			announce( 'Building ' + name + '. Choose modifiers, then Add to Order.' );

			if ( viaKeyboard && addBtn ) { /* keep focus on the box; controls follow in tab order */ }
		}

		function toggleBoost( chip ) {
			if ( ! activeBox ) { return; }
			var id = chip.getAttribute( 'data-op-id' );
			if ( activeBoosts[ id ] ) {
				delete activeBoosts[ id ];
				chip.classList.remove( 'is-active' );
				chip.setAttribute( 'aria-pressed', 'false' );
			} else {
				activeBoosts[ id ] = {
					id: parseInt( ( chip.getAttribute( 'data-op-id' ) || '' ).replace( 'mod-', '' ), 10 ) || 0,
					name: chip.getAttribute( 'data-op-name' ) || '',
					price: parseFloat( chip.getAttribute( 'data-op-price' ) ) || 0
				};
				chip.classList.add( 'is-active' );
				chip.setAttribute( 'aria-pressed', 'true' );
			}
			renderLineTotal();
		}

		// ---- Commit / lines ----
		function commitLine() {
			if ( ! activeBox ) { return; }
			var dotSrc = activeBox.querySelector( '.op-item__dot' );
			lines.push( {
				lineId: ++lineSeq,
				productId: parseInt( activeBox.getAttribute( 'data-op-id' ), 10 ) || 0,
				name: activeBox.getAttribute( 'data-op-name' ) || '',
				price: parseFloat( activeBox.getAttribute( 'data-op-price' ) ) || 0,
				tab: activeBox.getAttribute( 'data-op-tab' ),
				mod: activeModifier(),
				liquidId: activeLiquidId(),
				boosts: boostArray().map( function( b ) { return { id: b.id, name: b.name, price: b.price }; } ),
				dotColor: dotSrc ? getComputedStyle( dotSrc ).backgroundColor : ''
			} );
			var addedName = activeBox.getAttribute( 'data-op-name' ) || 'Item';
			clearActive( false );
			announce( addedName + ' added to your order.' );
			renderSummary();
		}

		function removeLine( lineId ) {
			lines = lines.filter( function( l ) { return l.lineId !== lineId; } );
			renderSummary();
		}

		function subtotal() {
			return lines.reduce( function( s, l ) {
				return s + l.price + l.boosts.reduce( function( bs, b ) { return bs + b.price; }, 0 );
			}, 0 );
		}

		function hasLines() { return lines.length > 0; }
		function nameFilled() { return nameInput && nameInput.value.trim() !== ''; }
		function phoneFilled() { return phoneInput && phoneInput.value.trim() !== ''; }

		// ---- Render summary ----
		function renderSummary() {
			summaryEl.textContent = '';
			if ( emptyEl ) { emptyEl.hidden = hasLines(); }

			lines.forEach( function( line ) {
				var li = document.createElement( 'li' );
				li.className = 'order-pad__line';

				// Main row: dot + name (modifier) + price + remove.
				var head = document.createElement( 'div' );
				head.className = 'order-pad__line-head';

				var main = document.createElement( 'span' );
				main.className = 'order-pad__line-main';

				var dot = document.createElement( 'span' );
				dot.className = 'order-pad__line-dot';
				dot.setAttribute( 'aria-hidden', 'true' );
				if ( line.dotColor ) { dot.style.backgroundColor = line.dotColor; }
				main.appendChild( dot );

				var nm = document.createElement( 'span' );
				nm.className = 'order-pad__line-name';
				nm.textContent = line.mod ? ( line.name + ' (' + line.mod + ')' ) : line.name;
				main.appendChild( nm );

				var price = document.createElement( 'span' );
				price.className = 'order-pad__line-price';
				price.textContent = money( line.price );

				var remove = document.createElement( 'button' );
				remove.type = 'button';
				remove.className = 'order-pad__remove';
				remove.setAttribute( 'aria-label', 'Remove ' + line.name + ' from your order' );
				remove.textContent = '×';
				remove.addEventListener( 'click', function() { removeLine( line.lineId ); } );

				head.appendChild( main );
				head.appendChild( price );
				head.appendChild( remove );
				li.appendChild( head );

				// Nested modifier (boost) rows.
				if ( line.boosts.length ) {
					var ul = document.createElement( 'ul' );
					ul.className = 'order-pad__line-mods';
					line.boosts.forEach( function( b ) {
						var mli = document.createElement( 'li' );
						mli.className = 'order-pad__line-mod';
						var mn = document.createElement( 'span' );
						mn.textContent = '+ ' + b.name;
						var mp = document.createElement( 'span' );
						mp.textContent = '+' + money( b.price );
						mli.appendChild( mn );
						mli.appendChild( mp );
						ul.appendChild( mli );
					} );
					li.appendChild( ul );
				}

				summaryEl.appendChild( li );
			} );

			if ( subtotalEl ) { subtotalEl.textContent = money( subtotal() ); }
			updateSubmit();
		}

		function updateSubmit() {
			var ready = hasLines() && nameFilled() && phoneFilled();
			submitBtn.disabled = ! ready;
			var label;
			if ( ! hasLines() ) {
				label = 'Add items to start';
			} else if ( ! ( nameFilled() && phoneFilled() ) ) {
				label = 'Add your name and phone';
			} else {
				label = ctaLabel;
			}
			if ( submitLabel ) { submitLabel.textContent = label; }
		}

		// ---- Validation + submit seam ----
		function showError( el, input, msg ) {
			if ( ! el ) { return; }
			el.textContent = msg; el.hidden = false;
			if ( input ) { input.setAttribute( 'aria-invalid', 'true' ); }
		}
		function clearError( el, input ) {
			if ( ! el ) { return; }
			el.textContent = ''; el.hidden = true;
			if ( input ) { input.removeAttribute( 'aria-invalid' ); }
		}

		// ---- Submit: nonce → validate + create → response ----
		var opNonce    = null;
		var submitting = false;

		function bmgOrderPadUrl( route ) {
			return '/wp-json/bmg/v1/order-pad/' + route;
		}

		function getNonce( forceRefetch ) {
			if ( opNonce && ! forceRefetch ) { return Promise.resolve( opNonce ); }
			return fetch( bmgOrderPadUrl( 'nonce' ), { headers: { 'Accept': 'application/json' } } )
				.then( function( r ) { return r.json(); } )
				.then( function( d ) { opNonce = ( d && d.nonce ) ? d.nonce : null; return opNonce; } );
		}

		// Target payload contract (Phase 2a validator): product IDs + quantity + honeypot.
		function buildPayload() {
			var hpEl = pad.querySelector( '[data-op-hp]' );
			return {
				hp: hpEl ? hpEl.value : '',
				name: nameInput ? nameInput.value.trim() : '',
				phone: phoneInput ? phoneInput.value.trim() : '',
				email: emailInput ? emailInput.value.trim() : '', // optional; '' means no confirmation email
				pickup_time: pickupInput ? pickupInput.value : '',
				special_notes: notesInput ? notesInput.value.trim() : '',
				line_items: lines.map( function( l ) {
					var mods = [];
					if ( l.liquidId ) { mods.push( { product_id: l.liquidId } ); }
					l.boosts.forEach( function( b ) { if ( b.id ) { mods.push( { product_id: b.id } ); } } );
					return { product_id: l.productId, quantity: 1, modifiers: mods };
				} ),
				client_total: subtotal()
			};
		}

		function postSubmit( nonce, payload ) {
			return fetch( bmgOrderPadUrl( 'submit' ), {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', 'X-BMG-Nonce': nonce || '' },
				body: JSON.stringify( payload )
			} ).then( function( r ) {
				return r.json().then( function( d ) { return { status: r.status, data: d }; } );
			} );
		}

		function setSubmitting( on ) {
			submitting = on;
			if ( on ) {
				submitBtn.disabled = true;
				if ( submitLabel ) { submitLabel.textContent = 'Placing…'; }
			} else {
				updateSubmit(); // restores label + disabled state from current form validity
			}
		}

		function submitReservation() {
			if ( submitting ) { return; }
			var payload = buildPayload();
			setSubmitting( true );
			getNonce( false )
				.then( function( nonce ) { return postSubmit( nonce, payload ); } )
				.then( function( res ) {
					// One silent retry on a stale/invalid nonce.
					if ( res.data && res.data.error_code === 'invalid_nonce' ) {
						return getNonce( true ).then( function( n ) { return postSubmit( n, payload ); } );
					}
					return res;
				} )
				.then( function( res ) { handleSubmitResponse( res ); } )
				.catch( function( err ) {
					console.error( '[order-pad] submit failed', err );
					handleSubmitResponse( { status: 0, data: { ok: false, error_code: 'network_error' } } );
				} );
		}

		// ---- Response UX: success / sold-out / field errors / other codes ----
		var lastSoldOut = [];

		function fmtMoney( n ) { return '$' + ( Math.round( ( Number( n ) || 0 ) * 100 ) / 100 ).toFixed( 2 ); }
		function fmtPickup( iso ) {
			if ( ! iso ) { return ''; }
			var d = new Date( iso );
			if ( isNaN( d.getTime() ) ) { return iso; }
			return 'Today at ' + d.toLocaleTimeString( [], { hour: 'numeric', minute: '2-digit' } );
		}

		function restoreSubmit() { updateSubmit(); }

		function clearAllErrors() {
			pad.querySelectorAll( '[data-op-error]' ).forEach( function( el ) { el.textContent = ''; el.hidden = true; } );
			[ nameInput, phoneInput, emailInput ].forEach( function( inp ) { if ( inp ) { inp.removeAttribute( 'aria-invalid' ); } } );
			var banner = pad.querySelector( '[data-op-error-banner]' );
			if ( banner ) { banner.textContent = ''; banner.hidden = true; }
		}

		function showBanner( msg ) {
			var banner = pad.querySelector( '[data-op-error-banner]' );
			if ( banner ) { banner.textContent = msg; banner.hidden = false; }
		}

		function showFieldErrors( errors ) {
			var lineMsgs = [];
			Object.keys( errors || {} ).forEach( function( field ) {
				var val = errors[ field ];
				var msg = Array.isArray( val ) ? val.join( ' ' ) : String( val );
				if ( field === 'name' || field === 'phone' || field === 'email' || field === 'pickup_time' || field === 'special_notes' ) {
					var el = pad.querySelector( '[data-op-error="' + field + '"]' );
					if ( el ) { el.textContent = msg; el.hidden = false; }
					var inputs = { name: nameInput, phone: phoneInput, email: emailInput };
					var input = inputs[ field ] || null;
					if ( input ) { input.setAttribute( 'aria-invalid', 'true' ); }
				} else {
					lineMsgs.push( msg ); // line_items.* (incl. a smoothie missing its liquid)
				}
			} );
			if ( lineMsgs.length ) { showBanner( lineMsgs.join( ' ' ) ); }
		}

		function showSuccess( d ) {
			var set = function( sel, txt ) { var el = pad.querySelector( sel ); if ( el ) { el.textContent = txt; } };
			set( '[data-op-success-number]', d.order_number || ( '#' + d.order_id ) );
			set( '[data-op-success-name]', nameInput ? nameInput.value.trim() : '' );
			set( '[data-op-success-pickup]', fmtPickup( d.pickup_time_iso ) );
			set( '[data-op-success-total]', fmtMoney( d.computed_total ) );
			var layout = pad.querySelector( '.order-pad__layout' );
			if ( layout ) { layout.hidden = true; }
			var succ = pad.querySelector( '[data-op-success]' );
			if ( succ ) {
				succ.hidden = false;
				if ( typeof succ.scrollIntoView === 'function' ) { succ.scrollIntoView( { block: 'center' } ); }
			}
		}

		function showSoldOut( items ) {
			lastSoldOut = items || [];
			var list = pad.querySelector( '[data-op-soldout-list]' );
			if ( list ) {
				list.textContent = '';
				lastSoldOut.forEach( function( it ) {
					var li = document.createElement( 'li' );
					li.textContent = it.name || ( '#' + it.product_id );
					list.appendChild( li );
				} );
			}
			var modal = pad.querySelector( '[data-op-soldout-modal]' );
			if ( modal ) { modal.hidden = false; }
		}

		function removeSoldOutFromOrder() {
			var ids = lastSoldOut.map( function( it ) { return parseInt( it.product_id, 10 ); } );
			lines = lines.filter( function( l ) {
				if ( ids.indexOf( l.productId ) !== -1 ) { return false; }
				if ( l.liquidId && ids.indexOf( l.liquidId ) !== -1 ) { return false; }
				for ( var i = 0; i < l.boosts.length; i++ ) { if ( ids.indexOf( l.boosts[ i ].id ) !== -1 ) { return false; } }
				return true;
			} );
			lastSoldOut = [];
			var modal = pad.querySelector( '[data-op-soldout-modal]' );
			if ( modal ) { modal.hidden = true; }
			renderSummary();
		}

		function freezeForm( ms ) {
			submitBtn.disabled = true;
			if ( submitLabel ) { submitLabel.textContent = 'Please wait…'; }
			setTimeout( function() { updateSubmit(); }, ms );
		}

		function resetOrderPad() {
			lines = [];
			clearActive( false );
			if ( nameInput ) { nameInput.value = ''; }
			if ( phoneInput ) { phoneInput.value = ''; }
			if ( notesInput ) { notesInput.value = ''; }
			if ( pickupInput && pickupInput.options.length ) { pickupInput.selectedIndex = 0; }
			var hpEl = pad.querySelector( '[data-op-hp]' ); if ( hpEl ) { hpEl.value = ''; }
			clearAllErrors();
			opNonce = null; // force a fresh nonce for the next order
			var succ = pad.querySelector( '[data-op-success]' ); if ( succ ) { succ.hidden = true; }
			var layout = pad.querySelector( '.order-pad__layout' ); if ( layout ) { layout.hidden = false; }
			var t0 = tabs.length ? tabs[ 0 ].getAttribute( 'data-op-tab' ) : '';
			if ( t0 ) { setTab( t0 ); }
			renderSummary();
		}

		function handleSubmitResponse( res ) {
			submitting = false;
			var d = res.data || {};
			console.log( '[order-pad] response', res.status, d );
			clearAllErrors();

			if ( d.ok && d.order_id ) { showSuccess( d ); return; } // success card takes over; button stays gone

			switch ( d.error_code ) {
				case 'items_sold_out':
					showSoldOut( d.sold_out || [] );
					restoreSubmit();
					break;
				case 'validation_failed':
					showFieldErrors( d.errors || {} );
					restoreSubmit();
					break;
				case 'rate_limited':
					showBanner( 'Too many attempts. Please wait a few minutes.' );
					restoreSubmit();
					break;
				case 'store_closed':
					showBanner( 'The store isn’t accepting online orders right now. Please call ' + ( pad.getAttribute( 'data-op-phone-contact' ) || '' ) + ' or stop by.' );
					restoreSubmit();
					break;
				case 'honeypot_tripped':
					freezeForm( 30000 ); // silent freeze; no success, no order
					break;
				default: // invalid_nonce (already retried), order_creation_failed, internal_error, network_error
					showBanner( 'Something went wrong. Please try again.' );
					restoreSubmit();
					break;
			}
		}

		// Wire the success / sold-out controls (present in the DOM, hidden until needed).
		( function() {
			var againBtn = pad.querySelector( '[data-op-again]' );
			if ( againBtn ) { againBtn.addEventListener( 'click', resetOrderPad ); }
			var soUpdate = pad.querySelector( '[data-op-soldout-update]' );
			if ( soUpdate ) { soUpdate.addEventListener( 'click', removeSoldOutFromOrder ); }
			var soDismiss = pad.querySelector( '[data-op-soldout-dismiss]' );
			if ( soDismiss ) { soDismiss.addEventListener( 'click', function() { var m = pad.querySelector( '[data-op-soldout-modal]' ); if ( m ) { m.hidden = true; } } ); }
		} )();

		submitBtn.addEventListener( 'click', function() {
			if ( submitBtn.disabled ) { return; }
			submitReservation();
		} );

		// ---- Wiring ----
		function setTab( tabId ) {
			clearActive( true ); // switching category discards the in-progress item
			tabs.forEach( function( t ) {
				var on = t.getAttribute( 'data-op-tab' ) === tabId;
				t.classList.toggle( 'is-active', on );
				t.setAttribute( 'aria-pressed', on ? 'true' : 'false' );
			} );
			itemBoxes.forEach( function( box ) {
				box.hidden = box.getAttribute( 'data-op-tab' ) !== tabId;
			} );
		}

		itemBoxes.forEach( function( box ) {
			box.addEventListener( 'click', function() { setActive( box, false ); } );
			box.addEventListener( 'keydown', function( e ) {
				if ( e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar' ) {
					e.preventDefault();
					setActive( box, true );
				}
			} );
		} );

		chips.forEach( function( chip ) {
			chip.addEventListener( 'click', function() { toggleBoost( chip ); } );
		} );

		pad.addEventListener( 'change', function( e ) {
			if ( e.target.matches( '[data-op-liquid]' ) ) { renderLineTotal(); }
		} );

		addBtn.addEventListener( 'click', commitLine );

		if ( nameInput ) { nameInput.addEventListener( 'input', function() { clearError( nameErr, nameInput ); updateSubmit(); } ); }
		if ( phoneInput ) { phoneInput.addEventListener( 'input', function() { clearError( phoneErr, phoneInput ); updateSubmit(); } ); }

		// The initial tab is already selected up top (before the ordering gate) so it
		// also works in read-only/closed mode; here we just paint the empty summary.
		renderSummary();
	}

	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', init );
	} else {
		init();
	}
} )();

/**
 * CBD listing filter — /cbd-products/.
 *
 * Client-side, no server round-trip and no URL change (locked). Hand-curated chips
 * filter the grid with OR logic: a "brand" chip matches data-brand; a "types" chip
 * matches any value in the pipe-joined data-types (so a product tagged both
 * "Full Spectrum" and "Sleep Support" surfaces under either). "All CBD" resets.
 * No-ops on any page without the grid.
 */
( function() {
	function init() {
		var grid = document.querySelector( '[data-cbd-grid]' );
		if ( ! grid ) {
			return;
		}
		var chips   = Array.prototype.slice.call( document.querySelectorAll( '.cbd-chip' ) );
		var items   = Array.prototype.slice.call( grid.querySelectorAll( '.cbd-grid__item' ) );
		var countEl = document.querySelector( '[data-cbd-count]' );
		var emptyEl = document.querySelector( '[data-cbd-empty]' );

		function matches( item, attr, value ) {
			if ( attr === 'all' ) {
				return true;
			}
			if ( attr === 'brand' ) {
				return ( item.getAttribute( 'data-brand' ) || '' ) === value;
			}
			// types — pipe-joined multi-value.
			var types = ( item.getAttribute( 'data-types' ) || '' ).split( '|' );
			return types.indexOf( value ) !== -1;
		}

		function apply( attr, value ) {
			var shown = 0;
			items.forEach( function( item ) {
				var show = matches( item, attr, value );
				item.classList.toggle( 'is-hidden', ! show );
				if ( show ) {
					shown++;
				}
			} );
			if ( countEl ) {
				countEl.textContent = String( shown );
			}
			if ( emptyEl ) {
				emptyEl.hidden = shown !== 0;
			}
		}

		chips.forEach( function( chip ) {
			chip.addEventListener( 'click', function() {
				chips.forEach( function( c ) {
					var active = c === chip;
					c.classList.toggle( 'is-active', active );
					c.setAttribute( 'aria-pressed', active ? 'true' : 'false' );
				} );
				apply( chip.getAttribute( 'data-filter-attr' ), chip.getAttribute( 'data-filter-value' ) || '' );
			} );
		} );
	}

	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', init );
	} else {
		init();
	}
} )();

/**
 * CBD age gate — /cbd-products/, CBD single products, and CBD cart/checkout.
 *
 * Session-cookie gate (bmg_age_confirmed=1, no expiry → clears when the browser
 * session ends). The server renders the modal only for unconfirmed visitors; this
 * module wires the buttons, traps focus, treats Escape as "No" (exit to home), and
 * re-checks the cookie on load so a cached page never re-shows the gate.
 */
( function() {
	function init() {
		var gate = document.getElementById( 'bmg-age-gate' );
		if ( ! gate ) {
			return;
		}

		// Cache-safe: already confirmed this session → remove without showing.
		if ( document.cookie.indexOf( 'bmg_age_confirmed=1' ) !== -1 ) {
			if ( gate.parentNode ) {
				gate.parentNode.removeChild( gate );
			}
			return;
		}

		var home     = gate.getAttribute( 'data-home' ) || '/';
		var yesBtn   = gate.querySelector( '[data-age-yes]' );
		var noBtn    = gate.querySelector( '[data-age-no]' );
		var focusable = [ yesBtn, noBtn ].filter( Boolean );
		var rootEl   = document.documentElement;
		var prevOverflow = rootEl.style.overflow;

		rootEl.style.overflow = 'hidden'; // block scroll while open

		function close() {
			rootEl.style.overflow = prevOverflow;
			document.removeEventListener( 'focusin', keepFocus, true );
			if ( gate.parentNode ) {
				gate.parentNode.removeChild( gate );
			}
		}

		function confirmAge() {
			// Session cookie — no expires attribute, so it dies with the session.
			document.cookie = 'bmg_age_confirmed=1; path=/; SameSite=Lax';
			close();
		}

		function exit() {
			window.location.href = home;
		}

		function keepFocus( e ) {
			if ( gate && ! gate.contains( e.target ) ) {
				( focusable[ 0 ] || gate ).focus();
			}
		}

		if ( yesBtn ) { yesBtn.addEventListener( 'click', confirmAge ); }
		if ( noBtn ) { noBtn.addEventListener( 'click', exit ); }

		gate.addEventListener( 'keydown', function( e ) {
			if ( e.key === 'Escape' ) {
				e.preventDefault();
				exit(); // Escape = "No"
			} else if ( e.key === 'Tab' && focusable.length ) {
				var first = focusable[ 0 ];
				var last  = focusable[ focusable.length - 1 ];
				if ( e.shiftKey && document.activeElement === first ) {
					e.preventDefault();
					last.focus();
				} else if ( ! e.shiftKey && document.activeElement === last ) {
					e.preventDefault();
					first.focus();
				}
			}
		} );

		document.addEventListener( 'focusin', keepFocus, true );
		( focusable[ 0 ] || gate ).focus();
	}

	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', init );
	} else {
		init();
	}
} )();

/**
 * Cart quantity stepper (Sunshine cart, woocommerce/cart/cart.php).
 *
 * The − / + buttons adjust the WooCommerce quantity input within its min/max, then
 * submit the cart form via the native "Update cart" button so WooCommerce recalculates
 * totals server-side. No-ops if the cart isn't present.
 */
( function() {
	function init() {
		var steppers = document.querySelectorAll( '.cbd-qty-step' );
		if ( ! steppers.length ) {
			return;
		}
		Array.prototype.forEach.call( steppers, function( btn ) {
			btn.addEventListener( 'click', function() {
				var wrap  = btn.closest( '.cbd-cart__qty' );
				var input = wrap && wrap.querySelector( 'input.qty, .quantity input' );
				if ( ! input ) {
					return;
				}
				var step = parseInt( btn.getAttribute( 'data-step' ), 10 ) || 0;
				var cur  = parseInt( input.value, 10 );
				if ( isNaN( cur ) ) { cur = 0; }
				var min = input.min !== '' ? parseInt( input.min, 10 ) : 0;
				var max = input.max !== '' ? parseInt( input.max, 10 ) : Infinity;
				var next = Math.min( max, Math.max( min, cur + step ) );
				if ( next === cur ) {
					return;
				}
				input.value = next;
				input.dispatchEvent( new Event( 'change', { bubbles: true } ) );

				var form   = btn.closest( 'form.woocommerce-cart-form' );
				var update = form && form.querySelector( '[name="update_cart"]' );
				if ( form && update ) {
					update.disabled = false;
					if ( typeof form.requestSubmit === 'function' ) {
						form.requestSubmit( update );
					} else {
						update.click();
					}
				}
			} );
		} );
	}

	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', init );
	} else {
		init();
	}
} )();

/**
 * Featured "Add to Order" → activate the product in the order pad.
 *
 * The homepage "Made Fresh, Grab & Go" cards carry data-product-id. Clicking
 * "Add to Order" switches the pad to that product's category tab, tap-selects the
 * matching item (reusing the pad's OWN tab + setActive handlers via synthetic
 * clicks — no duplicated state logic), scrolls the pad into view, and focuses the
 * activated card. If the product isn't in the pad (out of stock / removed), it
 * falls back to the default scroll and logs a warning.
 *
 * Bound in the CAPTURE phase on the featured section so it runs before the global
 * a[href="#order"] smooth-scroll; when it handles a click it stops propagation so
 * the pad isn't scrolled twice.
 */
( function() {
	function esc( v ) {
		return ( window.CSS && CSS.escape ) ? CSS.escape( v ) : v;
	}

	function init() {
		var pad      = document.querySelector( '.order-pad' );
		var featured = document.querySelector( '.featured-products' );
		if ( ! pad || ! featured ) {
			return;
		}
		var reduce = window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

		featured.addEventListener( 'click', function( e ) {
			var cta = e.target.closest ? e.target.closest( '.product-card__cta' ) : null;
			if ( ! cta || ! featured.contains( cta ) ) {
				return;
			}
			var card = cta.closest( '.product-card' );
			var pid  = card && card.getAttribute( 'data-product-id' );
			if ( ! pid ) {
				return; // no linked product → let the default #order scroll run
			}

			var item = pad.querySelector( '.op-item[data-op-id="' + esc( pid ) + '"]' );
			if ( ! item ) {
				console.warn( '[featured] No order-pad item for product id ' + pid + ' — scroll only.' );
				return; // fallback: default smooth-scroll to #order
			}

			// We handle it — stop the generic anchor smooth-scroll from also firing.
			e.preventDefault();
			e.stopPropagation();

			// 1. Switch to the item's category tab first (reuse the tab's handler).
			var tabId = item.getAttribute( 'data-op-tab' );
			if ( tabId ) {
				var tab = pad.querySelector( '.order-pad__tab[data-op-tab="' + esc( tabId ) + '"]' );
				if ( tab ) { tab.click(); }
			}
			// 2. Tap-select the item (reuse setActive) → active state + modifier chips.
			item.click();
			// 3. Smooth-scroll the pad into view (same header offset as the site anchors).
			var headerOffset = 80;
			var top = pad.getBoundingClientRect().top + window.pageYOffset - headerOffset;
			window.scrollTo( { top: top, behavior: reduce ? 'auto' : 'smooth' } );
			// 4. Move focus to the activated card (no extra scroll jump).
			if ( item.focus ) { item.focus( { preventScroll: true } ); }
		}, true );
	}

	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', init );
	} else {
		init();
	}
} )();

/**
 * Mobile Navigation — accessible opaque panel.
 *
 * The menu is a Bootstrap collapse (#navbarNavDropdown); Bootstrap owns the toggle,
 * aria-expanded, and the hamburger→X. This module layers the accessibility Bootstrap's
 * collapse does NOT provide: background-scroll lock while open, focus moved into the
 * panel on open and returned to the toggler on close, a focus trap (toggler ↔ links),
 * and Escape-to-close. The opaque header surface is handled in CSS via .is-menu-open,
 * so no hero content shows through. Desktop (≥lg) never toggles, so none of this runs.
 */
( function() {
	'use strict';

	function init() {
		var toggler = document.querySelector( '.sh-toggler' );
		var menu    = document.getElementById( 'navbarNavDropdown' );
		var nav     = document.getElementById( 'main-nav' );
		if ( ! toggler || ! menu || ! nav ) { return; }

		function isOpen() {
			return toggler.getAttribute( 'aria-expanded' ) === 'true';
		}

		// Trap set: the toggler (which is the close control) + every link in the panel.
		function focusables() {
			return [ toggler ].concat(
				Array.prototype.slice.call( menu.querySelectorAll( 'a[href], button:not([disabled])' ) )
			);
		}

		function closeMenu() {
			var inst = Collapse.getInstance( menu ) || new Collapse( menu, { toggle: false } );
			inst.hide();
		}

		menu.addEventListener( 'shown.bs.collapse', function() {
			document.documentElement.classList.add( 'sh-menu-locked' ); // lock background scroll
			nav.classList.add( 'is-menu-open' );                        // opaque header (CSS)
			var first = menu.querySelector( 'a[href], button' );
			if ( first ) { first.focus(); }
		} );

		menu.addEventListener( 'hidden.bs.collapse', function() {
			document.documentElement.classList.remove( 'sh-menu-locked' );
			nav.classList.remove( 'is-menu-open' );
			toggler.focus(); // return focus to the trigger
		} );

		// Escape closes; focus returns to the toggler via hidden.bs.collapse.
		document.addEventListener( 'keydown', function( e ) {
			if ( ( e.key === 'Escape' || e.key === 'Esc' ) && isOpen() ) {
				e.preventDefault();
				closeMenu();
			}
		} );

		// Keep Tab focus inside the open panel.
		document.addEventListener( 'keydown', function( e ) {
			if ( e.key !== 'Tab' || ! isOpen() ) { return; }
			var f = focusables();
			if ( f.length < 2 ) { return; }
			var firstEl = f[ 0 ];
			var lastEl  = f[ f.length - 1 ];
			if ( e.shiftKey && document.activeElement === firstEl ) {
				e.preventDefault();
				lastEl.focus();
			} else if ( ! e.shiftKey && document.activeElement === lastEl ) {
				e.preventDefault();
				firstEl.focus();
			}
		} );

		// If the viewport grows to desktop while the menu is open, drop the locks —
		// the panel becomes the inline desktop nav and never fires hidden.bs.collapse.
		window.addEventListener( 'resize', function() {
			if ( window.innerWidth >= 992 && nav.classList.contains( 'is-menu-open' ) ) {
				document.documentElement.classList.remove( 'sh-menu-locked' );
				nav.classList.remove( 'is-menu-open' );
			}
		} );
	}

	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', init );
	} else {
		init();
	}
} )();
